import React from "react";
import styles from "./Legend.module.css";

/**
 * A component to render a legend for a chart.
 * @param {string} direction - The direction of the legend (either "row" or "column").
 * @param {array} data - An array of objects containing the data to be displayed in the legend.
 *    Each object should have the following properties:
 *      id: {string} - The id of the item.
 *      label: {string} - The label of the item.
 *      value: {number} - The value of the item.
 *      color: {string} - The color of the item.
 * @param {string} unit - The unit of the values in the legend.
 * @param {boolean} isLoading - Indicates that data is being fetched
 */

const Legend = ({ direction, data, unit, isLoading = false, onItemClick }) => {
  if (isLoading) {
    return (
      <div
        className={styles.legendContainer}
        style={{ flexDirection: direction }}
      >
        {[1, 2, 3].map((i) => (
          <SkeletonLegendItem key={i} />
        ))}
      </div>
    );
  }

  return (
    <div
      className={styles.legendContainer}
      style={{ flexDirection: direction }}
    >
      {data.map((item) => {
        return (
          <LegendItem
            key={item.id}
            item={item}
            title={item.label || item.id}
            value={item.value}
            color={item.color}
            unit={unit}
            onItemClick={onItemClick}
          />
        );
      })}
    </div>
  );
};

const SkeletonLegendItem = () => {
  return (
    <div className={styles.legendItem}>
      <div className={styles.legendTitleContainer}>
        <span
          className={`${styles.legendItemColor} shimmer`}
          style={{ backgroundColor: "#f0f0f0" }} 
        ></span>
        <div
          className="shimmer"
          style={{ width: "6rem", height: "0.8rem", borderRadius: "4px" }}
        ></div>
      </div>
      <div
        className="shimmer"
        style={{ width: "2rem", height: "0.8rem", borderRadius: "4px" }}
      ></div>
    </div>
  );
};

const LegendItem = ({ item, title, value, color, unit, onItemClick }) => {
  const clickableProps = onItemClick
    ? {
        type: "button",
        onClick: (event) => {
          event.stopPropagation();
          onItemClick(item);
        },
      }
    : {};
  const Component = onItemClick ? "button" : "div";

  return (
    <Component className={styles.legendItem} {...clickableProps}>
      <div className={styles.legendTitleContainer}>
        <span
          className={styles.legendItemColor}
          style={{ backgroundColor: color }}
        ></span>
        <div className={styles.legendItemTitle}>{title}</div>
      </div>
      <div className={styles.legendItemValue}>{`${value} ${unit || ""}`}</div>
    </Component>
  );
};

export default Legend;
